// SelectedModelPanel - 選択中アセットのプレビュー＋回転パネル（左下常駐）
// spec.md §5.8.9: タイトル「選択中のアセット」＋ 〔プレビュー｜左右回転(90°)〕

import { useDispatch, useSelector } from "react-redux";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useScale, usePlaySound } from "common/utils";
import { useBuilderScene } from "features/builder/context";
import { actions, selectMapModelById } from "features/builder/slice";

const PANEL_BASE_WIDTH = 216;
const PANEL_BASE_HEIGHT = 181;

export const SelectedModelPanel = () => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const scene = useBuilderScene();
  const { scale } = useScale();

  const selectedPlacedModelId = useSelector(
    (state: RootState) => state.builder.selectedPlacedModelId,
  );
  const placedModel = useSelector((state: RootState) =>
    selectedPlacedModelId ? selectMapModelById(state, selectedPlacedModelId) : null,
  );
  // これから置くブロック。置いたブロックを選んでいないときは、こちらを見せる。
  // 選んだのに何も出ないと、いま何を持っているのか分からない
  const heldAsset = useSelector((state: RootState) => state.builder.selectedAssetModel);
  const selectedModel = placedModel ?? heldAsset;
  // 回転できるのは置いたブロックだけ（まだ置いていないブロックには回すものが無い）
  const canRotate = !!placedModel;

  if (!selectedModel) return null;

  const handleRotate = (dir: 1 | -1) => () => {
    play();
    if (!scene || !selectedPlacedModelId) return;
    const mesh = scene.getMeshById(selectedPlacedModelId) as Mesh | null;
    if (mesh?.parent) {
      const outer = mesh.parent as TransformNode;
      outer.addRotation(0, (dir * Math.PI) / 2, 0);
      dispatch(
        actions.updateMapModel({
          id: selectedPlacedModelId,
          changes: {
            direction: {
              x: Math.round(outer.forward.x),
              y: 0,
              z: Math.round(outer.forward.z),
            },
          },
        }),
      );
    }
  };

  // アセット画像のパス
  const assetImagePath = `${Constants.assetHost}/assets/models/${selectedModel.filename.replace(".gltf.glb", ".png").replace(".glb", ".png")}`;

  return (
    <div
      className="flex-col-el absolute left-0 z-[10000] pointer-events-none"
      style={{ bottom: 8 * scale }}
    >
      <div
        style={{
          width: PANEL_BASE_WIDTH * scale,
          height: PANEL_BASE_HEIGHT * scale,
          marginLeft: 8 * scale,
        }}
        className="pointer-events-auto"
      >
        <div
          style={{
            width: PANEL_BASE_WIDTH,
            height: PANEL_BASE_HEIGHT,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el origin-top-left"
        >
          <div className="flex-col-el items-stretch gap-2 w-full p-3 box-border rounded-2xl bg-white border-2 border-white shadow-lg">
            {/* タイトル */}
            <span className="type-sm text-textcolor-black text-center whitespace-nowrap">
              {I18n.t("MSG_BUILDER_SELECTED_ASSET")}
            </span>

            {/* プレビュー ＋ 回転ボタン */}
            <div className="flex-row-el items-center gap-2">
              <div
                className={`flex-col-el flex-center relative h-28 box-border rounded-xl bg-gray-10 border-2 border-gray-20 overflow-hidden ${
                  canRotate ? "w-28" : "w-full"
                }`}
              >
                <img
                  src={assetImagePath}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>

              {canRotate && (
                <div className="flex-col-el gap-2">
                  <button
                    onClick={handleRotate(-1)}
                    className="flex-col-el flex-center w-14 h-14 box-border rounded-xl bg-white border-2 border-blue-100 transition-all duration-150 active:scale-95 cursor-pointer type-inherit-font"
                  >
                    <span className="flex-col-el flex-center type-title text-blue-100">
                      <RotateLeftIcon fontSize="inherit" />
                    </span>
                  </button>
                  <button
                    onClick={handleRotate(1)}
                    className="flex-col-el flex-center w-14 h-14 box-border rounded-xl bg-white border-2 border-blue-100 transition-all duration-150 active:scale-95 cursor-pointer type-inherit-font"
                  >
                    <span className="flex-col-el flex-center type-title text-blue-100">
                      <RotateRightIcon fontSize="inherit" />
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
